import React from 'react';
import { BaseModal, EMPTY_MODAL_FOOTER, EMPTY_MODAL_FOOTER_STYLE } from '@/components/residents/marketplace/shared';
import { CreditCardFormContent } from './content/CreditCardFormContent';
import { ACHFormContent } from './content/ACHFormContent';

type FormType = 'card' | 'ach';

interface PaymentFormModalProps {
  visible: boolean;
  onClose: () => void;
  onMethodAdded: (methodId?: number) => void;
  formType: FormType;
}

const FORM_TITLES: Record<FormType, string> = {
  card: 'Credit or debit card details',
  ach: 'ACH details',
};

/**
 * Unified Payment Form Modal
 * 
 * This modal wrapper reuses the content components from the Make Payment flow
 * to ensure consistent styling, keyboard handling, and footer behavior.
 * 
 * Used by Account Settings to add new payment methods.
 */
export const PaymentFormModal: React.FC<PaymentFormModalProps> = ({
  visible,
  onClose,
  onMethodAdded,
  formType,
}) => {
  const title = FORM_TITLES[formType];

  const handleMethodAdded = (methodId?: number) => {
    onMethodAdded(methodId);
  };

  const renderContent = () => {
    switch (formType) {
      case 'card':
        return (
          <CreditCardFormContent
            onCardAdded={handleMethodAdded}
            onBack={onClose}
          />
        );
      case 'ach':
        return (
          <ACHFormContent
            onACHAdded={handleMethodAdded}
            onBack={onClose}
          />
        );
      default:
        return null;
    }
  };

  return (
    <BaseModal
      visible={visible}
      onClose={onClose}
      title={title}
      centerTitle
      avoidKeyboard
      scrollable
      contentContainerStyle={scrollContentOverride}
      footer={EMPTY_MODAL_FOOTER}
      footerStyle={EMPTY_MODAL_FOOTER_STYLE}
      animationType="none"
    >
      {renderContent()}
    </BaseModal>
  );
};

const scrollContentOverride = { paddingHorizontal: 0, paddingBottom: 0, flexGrow: 1 };

export default PaymentFormModal;
